import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { Button } from './ui/button';
import {
  Menu, X, MessageCircle, UserPlus, Calendar, Search,
  ChevronDown, Newspaper, GraduationCap, Handshake,
  Home, Briefcase, User, Play, Star, HelpCircle, Mail, BookOpen, Banknote,
  ShieldCheck, Cpu, MapPinned, Library, Compass, FileText,
} from 'lucide-react';
import { contactData } from '../data/mock';
import { useBookingModal } from '../context/BookingContext';
import { useSiteSearch } from '../context/SearchContext';
import Logo from './Logo';
import LiveAvailabilityStrip from './LiveAvailabilityStrip';
import MoveAnnouncementBanner from './MoveAnnouncementBanner';
import useSitePages from '../hooks/useSitePages';
import './live-strip.css';
import './nav-more.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeRoute = location.pathname === '/';
  const { openBooking } = useBookingModal();
  const { openSearch } = useSiteSearch();
  const sitePages = useSitePages();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMoreOpen) return undefined;
    const handleOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setIsMoreOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isMoreOpen]);

  const navLinks = [
    { label: 'Home', href: '#home', icon: Home },
    { label: 'Services', href: '#services', icon: Briefcase },
    { label: 'About', href: '#about', icon: User },
    { label: 'Videos', href: '/videos', isRoute: true, icon: Play },
    { label: 'Reviews', href: '#testimonials', icon: Star },
    { label: 'FAQ', href: '#faq', icon: HelpCircle },
    { label: 'Contact', href: '#contact', icon: Mail }
  ];

  const moreLinks = [
    { label: 'What to Expect', href: '/what-to-expect', icon: Compass, blurb: 'Outcomes, timelines & fees' },
    { label: 'Our Standard', href: '/the-tmd-physio-standard', icon: ShieldCheck, blurb: 'Our published standard of care' },
    { label: 'Technology', href: '/technology-and-treatments', icon: Cpu, blurb: 'NESA, laser, dry needling & more' },
    { label: 'Evidence Library', href: '/evidence-library', icon: Library, blurb: 'The research behind our care' },
    { label: 'Areas We Serve', href: '/areas-we-serve', icon: MapPinned, blurb: 'Journey times to Waterloo' },
    { label: 'TMD Knowledge Base', href: '/tmd-knowledge-base', icon: BookOpen, blurb: 'Articles & guides' },
    { label: 'Fees', href: '/fees', icon: Banknote, blurb: 'Transparent pricing' },
    { label: 'Media', href: '/media', icon: Newspaper, blurb: 'Press, podcasts & features' },
    { label: 'Courses', href: '/courses', icon: GraduationCap, blurb: 'CPD training for clinicians' },
    { label: 'Partners', href: '/partners', icon: Handshake, blurb: 'Our trusted network' },
    ...sitePages.filter((p) => p.in_menu).map((p) => ({ label: p.nav_label, href: `/${p.slug}`, icon: FileText, blurb: p.nav_blurb })),
  ];

  const handleNavClick = (href, isRoute) => {
    setIsMobileMenuOpen(false);

    // Absolute paths → SPA route.
    if (isRoute || (typeof href === 'string' && href.startsWith('/'))) {
      navigate(href);
      if (href === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Anchor links (#home, #services, etc.) — if we're already on the home
    // page, smooth-scroll to the anchor. Otherwise SPA-navigate to '/' and
    // then scroll to the anchor after mount.
    // NB: we can't rely on `document.querySelector(href)` to detect "am I on
    // the right page?" because App.js:140 wraps every route in `<div
    // id="home">` — that selector would spuriously match on every subpage and
    // just scroll to the top instead of navigating home.
    if (typeof href === 'string' && href.startsWith('#')) {
      if (isHomeRoute) {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      navigate('/');
      // The homepage sections mount asynchronously; poll for up to 2s until
      // the anchor exists, then smooth-scroll. This is more reliable than a
      // fixed setTimeout (which was racing React Router + component mount).
      let attempts = 0;
      const maxAttempts = 40; // 40 * 50ms = 2s
      const poll = () => {
        attempts += 1;
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        if (attempts >= maxAttempts) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        setTimeout(poll, 50);
      };
      setTimeout(poll, 50);
      return;
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}?text=Hello! I'd like to book a consultation`, '_blank');
  };

  const handleRefer = () => {
    setIsMobileMenuOpen(false);
    navigate('/refer');
  };

  const handleBookNow = () => {
    setIsMobileMenuOpen(false);
    openBooking();
  };

  return (
    <>
      <MoveAnnouncementBanner />
      <LiveAvailabilityStrip />
      <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
        <div className="header-container">
          <button 
            className="header-logo"
            onClick={() => handleNavClick('#home')}
          >
            <Logo />
          </button>

          <nav className="header-nav desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href, link.isRoute)}
                className="nav-link"
              >
                {link.label}
              </button>
            ))}
            <div className="nav-more-wrap" ref={moreRef}>
              <button
                type="button"
                className="nav-link nav-more-trigger"
                onClick={() => setIsMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={isMoreOpen}
                data-testid="nav-more-trigger"
              >
                More
                <ChevronDown size={14} style={{ transform: isMoreOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s ease' }} />
              </button>
              {isMoreOpen ? (
                <div className="nav-more-menu" role="menu" data-testid="nav-more-menu">
                  {moreLinks.map((m) => {
                    const Icon = m.icon;
                    return (
                      <Link
                        key={m.label}
                        to={m.href}
                        role="menuitem"
                        className="nav-more-item"
                        onClick={() => {
                          // Force the dropdown to unmount in its own commit
                          // BEFORE the route transition batches. Without
                          // flushSync, React 18 batches both state updates
                          // (close + navigate), which then races against
                          // fiber deletion when the new route also renders
                          // — producing a spurious removeChild NotFoundError
                          // in dev mode.
                          flushSync(() => setIsMoreOpen(false));
                        }}
                        data-testid={`nav-more-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="nav-more-item-icon"><Icon size={16} /></span>
                        <span className="nav-more-item-content">
                          <strong>{m.label}</strong>
                          <small>{m.blurb}</small>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </nav>

          <div className="header-actions">
            <button
              type="button"
              className="header-search-trigger desktop-only"
              onClick={openSearch}
              aria-label="Search site"
              data-testid="header-search-trigger"
            >
              <Search size={16} />
              <span className="header-search-label">Search</span>
              <kbd className="header-search-kbd">⌘K</kbd>
            </button>
            <Button
              variant="outline"
              className="btn-refer desktop-only"
              onClick={handleRefer}
              data-testid="header-refer-button"
            >
              <UserPlus className="w-4 h-4" />
              Refer a Patient
            </Button>

            <Button
              className="btn-book-now desktop-only"
              onClick={handleBookNow}
              data-testid="header-book-now-button"
            >
              <Calendar className="w-4 h-4" />
              <span className="btn-stacked-label">
                <span>Book</span>
                <span>Now</span>
              </span>
            </Button>

            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu — grid layout with teal gradient backdrop */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <div className="mobile-menu-inner">
          <div className="mobile-menu-grid" data-testid="mobile-nav-grid">
            {[...navLinks, ...moreLinks].map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => handleNavClick(link.href, link.isRoute)}
                  className="mobile-nav-tile"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="mobile-nav-tile-icon">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="mobile-nav-tile-label">{link.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mobile-menu-cta-row">
            <Button
              variant="outline"
              className="mobile-refer-btn"
              onClick={handleRefer}
              data-testid="mobile-refer-button"
            >
              <UserPlus className="w-4 h-4" />
              Refer a Patient
            </Button>
            <Button
              className="mobile-book-btn"
              onClick={handleBookNow}
              data-testid="mobile-book-now-button"
            >
              <Calendar className="w-4 h-4" />
              Book Now
            </Button>
            <Button
              className="mobile-cta-btn"
              onClick={handleWhatsApp}
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
